/**
 * NewsItemView — view da Notícia (News Item estendido).
 *
 * Cromagem fixa (Proposta Layout 02-web / 01-web-1): imagem de destaque,
 * corpo em blocos, data de publicação + share, "Veja Também" e, por último, a
 * faixa institucional (ver `splitInstitutionalBlurb`). Sem box de autor — a
 * autoria não é exibida (decisão 2026-09-08).
 *
 * Título e subtítulo NÃO são desenhados aqui: são os blocos nativos `title` e
 * `description` do Volto, que leem os mesmos campos do conteúdo mas podem ser
 * editados direto no canvas (decisão 2026-09-08). Eles vêm no início de
 * `blocks_layout`, então saem antes do corpo do artigo.
 *
 * Registrada em config.views.contentTypesViews['News Item'].
 */
import { defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import FormattedDate from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import Image from '@plone/volto/components/theme/Image/Image';
import { hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import ShareButtons from '../ShareButtons';
import RelatedNews from '../RelatedNews';

const messages = defineMessages({
  publishedOn: {
    id: 'newsItem.publishedOn',
    defaultMessage: 'Published on',
  },
  modifiedOn: {
    id: 'newsItem.modifiedOn',
    defaultMessage: 'Modified on',
  },
});

type NewsItemContent = {
  '@id': string;
  title?: string;
  description?: string;
  effective?: string;
  modified?: string;
  subjects?: string[];
  creators?: string[];
  image?: object;
  image_caption?: string;
  text?: { data?: string };
  [key: string]: unknown;
};

const BLURB_BLOCK_TYPE = 'institutionalBlurb';

/**
 * Separa a faixa institucional do resto dos blocos.
 *
 * A faixa é um bloco comum — o editor a edita no canvas, como qualquer outro
 * (docs/specs/blocks/institutional-blurb.md). Mas o lugar dela na PÁGINA é
 * fixo: vai depois da data, do share e do "Veja Também", que são cromagem da
 * view e não blocos. Renderizada no fluxo do corpo, ela caía antes deles.
 *
 * Por isso a view a extrai e a desenha por último. Consequência assumida: se o
 * editor arrastar a faixa para o meio do artigo, ela continua saindo no fim —
 * é uma faixa de posição fixa, e um "só move se for o último bloco" seria
 * imprevisível. Decisão de 2026-09-10.
 */
const splitInstitutionalBlurb = (content: NewsItemContent) => {
  const items = (content.blocks_layout as { items?: string[] })?.items;
  const blocks = content.blocks as Record<string, { '@type'?: string }>;
  if (!items || !blocks) return [content, null] as const;

  const daFaixa = items.filter(
    (id) => blocks[id]?.['@type'] === BLURB_BLOCK_TYPE,
  );
  if (daFaixa.length === 0) return [content, null] as const;

  const comItens = (lista: string[]) => ({
    ...content,
    blocks_layout: { ...(content.blocks_layout as object), items: lista },
  });
  return [
    comItens(items.filter((id) => !daFaixa.includes(id))),
    comItens(daFaixa),
  ] as const;
};

const NewsItemView = ({ content }: { content: NewsItemContent }) => {
  const intl = useIntl();
  const location = useLocation();
  const [corpo, faixaInstitucional] = splitInstitutionalBlurb(content);

  return (
    <article className="news-item-view">
      {content.image && (
        <figure className="news-item-view__media">
          <Image
            item={content}
            imageField="image"
            alt={content.image_caption || ''}
            loading="eager"
            responsive
            // Sem `sizes` o Image do core deixa o navegador assumir 100vw e
            // baixar a escala do tamanho da janela; a figura para em 1130px.
            sizes="(max-width: 1129px) 100vw, 1130px"
          />
          {content.image_caption && (
            <figcaption className="news-item-view__caption">
              {content.image_caption}
            </figcaption>
          )}
        </figure>
      )}

      <div className="news-item-view__body">
        {hasBlocksData(content) ? (
          <RenderBlocks content={corpo} />
        ) : (
          content.text?.data && (
            /* Conteúdo legado sem blocos (rich text HTML do Plone) */
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: content.text.data }}
            />
          )
        )}
      </div>

      <footer className="news-item-view__footer">
        <div className="news-item-view__dates">
          {content.effective && (
            <span className="news-item-view__date">
              {intl.formatMessage(messages.publishedOn)}{' '}
              <FormattedDate date={content.effective} />
            </span>
          )}
          {content.modified && (
            <span className="news-item-view__date news-item-view__date--modified">
              {intl.formatMessage(messages.modifiedOn)}{' '}
              <FormattedDate date={content.modified} />
            </span>
          )}
        </div>
        <ShareButtons title={content.title} />
      </footer>

      <RelatedNews
        currentPath={location.pathname}
        subjects={content.subjects}
      />

      {faixaInstitucional && <RenderBlocks content={faixaInstitucional} />}
    </article>
  );
};

export default NewsItemView;
